import React, { Component } from 'react'
import { Badge, Table, UncontrolledTooltip } from 'reactstrap'
import { graphql } from '@apollo/client/react/hoc'
import { Link } from "react-router-dom"
import PrimaryListingContainer from "../../Material/PrimaryListingContainer"
import DatetimeRenderer from "../../Material/DatetimeRenderer"
import RepAvatar from "../../Material/RepAvatar"
import toTitleCase from "../../Functions/toTitleCase"
import withApolloClient from '../../withApolloClient'
import { useAdvancedFilters } from "../../Filters/filterUrl"
import { AllCarInspections } from "./Queries"
import CarInspectionPicturesModal from "./CarInspectionPicturesModal"
import "../../Material/ListingHeader.css"
import "./CarInspections.css"

const tableHeaders = [
    // Frozen: stays pinned, with the row counter, while the table scrolls sideways.
    { id: "id", name: "ID", sortable: true, frozen: true },
    { id: "date_added", name: "Date Added", sortable: true },
    { id: "car__id", name: "Car", sortable: true },
    { id: "form_type", name: "Type", sortable: false },
    { id: "rental__driver", name: "Driver", sortable: false },
    { id: "mileage", name: "Mileage", sortable: true },
    { id: "has_incidents", name: "Incident", sortable: false },
    { id: "picture_count", name: "Pictures", sortable: false },
    { id: "user", name: "Inspected By", sortable: false },
    { id: "car__branch", name: "Branch", sortable: false },
]

// Opens the inspection in the car's Inspections tab, which already renders the
// full report, pictures and damages.
const inspectionLink = (inspection) => `/car/${inspection.car.pk}?inspectionId=${inspection.id}#inspections`

// "2026 TOYOTA CR-V" -> "2026 Toyota CR-V": only the make is capitalized, the
// model name is shown as stored.
const capitalize = (text) => text ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase() : text
const carName = (car) => [car.year, capitalize(car.carModel?.make), car.carModel?.name].filter(Boolean).join(" ")

class CarInspectionsList extends Component {
    state = { picturesInspection: null }

    renderSortIcon = (headerId) => {
        const orderBy = this.props.orderBy
        // Exact match: a substring check would light up "id" when sorting by "car__id".
        if (orderBy.includes(headerId)) return <i className="fa fa-sort-asc" />
        if (orderBy.includes("-" + headerId)) return <i className="fa fa-sort-desc" />
        return <i className="fa fa-sort" />
    }

    render() {
        const allCarConditions = this.props.allCarConditions
        return (
            <PrimaryListingContainer listLoading={this.props.loading} totalFetchedRows={allCarConditions && allCarConditions.edges.length} pageInfo={allCarConditions && allCarConditions.pageInfo} loadMoreEntries={allCarConditions && allCarConditions.pageInfo.hasNextPage ? this.props.loadMoreEntries : null} refetchListQuery={this.props.refetchQuery}>
                <Table responsive>
                    <thead>
                        <tr>
                            <th className="car-inspections-frozen car-inspections-frozen-counter">#</th>
                            {tableHeaders.map(tableHeader => {
                                const frozenClass = tableHeader.frozen ? "car-inspections-frozen car-inspections-frozen-id" : ""
                                return tableHeader.sortable ?
                                    <th key={tableHeader.id} className={"car-inspections-sortable-th " + frozenClass} onClick={() => this.props.updateOrder(tableHeader.id)}>{this.renderSortIcon(tableHeader.id)}&nbsp;&nbsp;{tableHeader.name}</th> :
                                    <th key={tableHeader.id} className={frozenClass}>{tableHeader.name}</th>
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {allCarConditions && allCarConditions.edges.length > 0 ? allCarConditions.edges.map(({ node: inspection }, i) =>
                            <tr key={inspection.id} className={inspection.cancellationReason ? "car-inspections-canceled-row" : ""}>
                                <td className="car-inspections-frozen car-inspections-frozen-counter"><span className="listing-counter">{i + 1}</span></td>
                                <td className="car-inspections-frozen car-inspections-frozen-id"><Link to={inspectionLink(inspection)}>VGM-{inspection.pk}</Link></td>
                                <td><DatetimeRenderer datetime={inspection.dateAdded} placement="left" /></td>
                                <td>
                                    <Link to={"/car/" + inspection.car.pk}>{carName(inspection.car) || "--"}</Link>
                                </td>
                                <td>
                                    {inspection.formType ? toTitleCase(inspection.formType) : "--"}
                                    {inspection.cancellationReason && <>
                                        <br />
                                        <Badge id={"inspectionCanceled" + i} className="bg-danger">Canceled</Badge>
                                        <UncontrolledTooltip target={"inspectionCanceled" + i} placement="bottom">{inspection.cancellationReason}</UncontrolledTooltip>
                                    </>}
                                </td>
                                <td>
                                    {inspection.rental?.driver ?
                                        <Link to={"/driver/" + inspection.rental.driver.id}>{inspection.rental.driver.name}</Link> : "--"}
                                </td>
                                <td>{inspection.mileage != null ? inspection.mileage.toLocaleString() : "--"}</td>
                                <td>{inspection.hasIncidents ? <Badge className="bg-warning">Yes</Badge> : "No"}</td>
                                <td>
                                    {inspection.pictureCount > 0 ? <>
                                        <button type="button" id={"viewPictures" + i} className="car-inspections-pictures" onClick={() => this.setState({ picturesInspection: inspection })}>
                                            <span className="car-inspections-pictures-stack">
                                                {(inspection.previewPictures || []).map(url =>
                                                    <img key={url} src={url} alt="" loading="lazy" />
                                                )}
                                            </span>
                                            <span className="car-inspections-pictures-count">
                                                <i className="fa fa-camera" aria-hidden="true" /> {inspection.pictureCount}
                                            </span>
                                        </button>
                                        <UncontrolledTooltip target={"viewPictures" + i} placement="bottom">View {inspection.pictureCount} {inspection.pictureCount === 1 ? "picture" : "pictures"}</UncontrolledTooltip>
                                    </> :
                                        <span className="car-inspections-no-pictures"><i className="fa fa-camera" aria-hidden="true" /> No photos</span>}
                                </td>
                                <td>{inspection.user ? <RepAvatar rep={inspection.user} /> : "--"}</td>
                                <td><span className="bos-badge bos-badge-blue">{inspection.car.branch?.name || "No Branch"}</span></td>
                            </tr>
                        ) : !this.props.loading && <tr>
                            <td colSpan={tableHeaders.length + 1}>No Inspections Found</td>
                        </tr>}
                    </tbody>
                </Table>
                {this.state.picturesInspection &&
                    <CarInspectionPicturesModal
                        open
                        carConditionId={this.state.picturesInspection.id}
                        inspection={this.state.picturesInspection}
                        carLabel={carName(this.state.picturesInspection.car)}
                        handleClose={() => this.setState({ picturesInspection: null })}
                    />}
            </PrimaryListingContainer>
        )
    }
}

const CarInspectionsListWithData = graphql(AllCarInspections, {
    options: ({ searchTerm, orderBy, filters }) => ({
        variables: { searchTerm, orderBy, filters, first: 30 },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: "cache-and-network",
    }),
    props: ({ data: { loading, allCarConditions, fetchMore, variables } }) => ({
        loading,
        allCarConditions,
        refetchQuery: () => {
            // Refetch in place, keeping however many rows have been loaded so far.
            return fetchMore({
                query: AllCarInspections,
                variables: { ...variables, first: allCarConditions ? allCarConditions.edges.length : 30 },
                updateQuery: (previousResult, { fetchMoreResult }) => ({
                    allCarConditions: {
                        ...fetchMoreResult.allCarConditions,
                        __typename: previousResult.allCarConditions.__typename,
                    },
                }),
            })
        },
        loadMoreEntries: () => {
            return fetchMore({
                query: AllCarInspections,
                variables: { ...variables, cursor: allCarConditions.pageInfo.endCursor },
                updateQuery: (previousResult, { fetchMoreResult }) => ({
                    allCarConditions: {
                        edges: [...previousResult.allCarConditions.edges, ...fetchMoreResult.allCarConditions.edges],
                        pageInfo: fetchMoreResult.allCarConditions.pageInfo,
                        __typename: previousResult.allCarConditions.__typename,
                    },
                }),
            })
        },
    }),
})(withApolloClient(CarInspectionsList))

// Advanced filters live in the URL; feed them as the single `filters` variable.
export default function CarInspectionsListWithFilters(props) {
    const [advancedFilters] = useAdvancedFilters()
    return <CarInspectionsListWithData {...props} filters={advancedFilters} />
}
